/**
 * Pills domain — the first-class arrangement pills the user manipulates
 * on the song timeline, plus the arrangement-segment list they live on.
 */
import type { Pill, Segment } from "../../core/types";
import { clamp } from "../../lib/clamp";
import type { SliceCreator } from "./state";

/** Minimum arr-window length for a pill — anything narrower is the user
 *  in the middle of a trim drag, not a usable chunk. Keep above the
 *  decoder's typical seek granularity so playback can land on it. */
export const PILL_MIN_WINDOW_S = 0.05;

/**
 * Find the arr-time bounds within which a pill can move in its cam-lane
 * without overlapping a same-cam neighbour. Pills derselben cam can't
 * occupy the same arr-time range — they're sliced through `cuts` to
 * pick which one is on PROGRAM. The pill being moved/trimmed is excluded
 * so its own current placement doesn't pin itself.
 */
function neighbourBoundsForCam(
  pills: readonly Pill[],
  me: Pill,
): { prevEnd: number; nextStart: number } {
  let prevEnd = 0;
  let nextStart = Number.POSITIVE_INFINITY;
  for (const sib of pills) {
    if (sib.id === me.id) continue;
    if (sib.camId !== me.camId) continue;
    // Sibling sits left of me — its right edge bounds my left.
    if (sib.arrEndS <= me.arrStartS && sib.arrEndS > prevEnd) {
      prevEnd = sib.arrEndS;
    }
    // Sibling sits right of me — its left edge bounds my right.
    if (sib.arrStartS >= me.arrEndS && sib.arrStartS < nextStart) {
      nextStart = sib.arrStartS;
    }
  }
  return { prevEnd, nextStart };
}

/** Restore a pill to its auto-generated arr-window + source-trim. Used
 *  by both per-pill (`resetPill`) and per-cam (`resetPillsForCam`)
 *  reset paths so they share one definition of "baseline". */
function restorePillBaseline(p: Pill): Pill {
  return {
    ...p,
    arrStartS: p.originalArrStartS,
    arrEndS: p.originalArrEndS,
    sourceInS: p.originalSourceInS,
    sourceOutS: p.originalSourceOutS,
    userEdited: false,
  };
}

/** Clamp a source-shift so `inS + deltaS` can't go negative, then return
 *  the APPLIED delta. Both window edges must move by this same applied
 *  amount — clamping the edges independently (the old pattern) moved
 *  them by different deltas, silently shrinking the source window while
 *  the pill's arr window stayed put: the 1:1 arr↔source mapping broke
 *  and playback ran off-rate with drifting alignment (#103). */
function clampSourceShift(inS: number, deltaS: number): number {
  return Math.max(deltaS, -inS);
}

/** Shift a pill's source-trim by `deltaS`, clamped against zero while
 *  ALWAYS preserving the window length. Shared by `nudgePillSourceMs`
 *  and `nudgeCamSourceMs` (which fans this out across the cam's
 *  pills). */
export function shiftPillSource(p: Pill, deltaS: number): Pill {
  const applied = clampSourceShift(p.sourceInS, deltaS);
  return {
    ...p,
    sourceInS: p.sourceInS + applied,
    sourceOutS: p.sourceOutS + applied,
  };
}

/** Shift a pill's ORIGINAL source bounds (the RESET baseline) by
 *  `deltaS` with the same length-preserving clamp as
 *  `shiftPillSource`. Used by the cam-anchor mutations
 *  (`setClipSyncOverride`, `setSelectedCandidateIdx`) so a per-pill
 *  RESET restores a baseline that's consistent with the new anchor. */
export function shiftPillOriginalSource(p: Pill, deltaS: number): Pill {
  const applied = clampSourceShift(p.originalSourceInS, deltaS);
  return {
    ...p,
    originalSourceInS: p.originalSourceInS + applied,
    originalSourceOutS: p.originalSourceOutS + applied,
  };
}

/** Pure helper — apply `fn` to the one pill matching `id` and return a
 *  new pills array. Other pills are returned by reference so React's
 *  shallow comparison + rerender machinery only re-keys the changed
 *  one. Centralised so every per-pill setter has the same shape and we
 *  don't sprinkle `.map(p => p.id === id ? {...} : p)` across the
 *  store. */
function mutatePill(
  pills: readonly Pill[],
  id: string,
  fn: (p: Pill) => Pill,
): Pill[] {
  return pills.map((p) => (p.id === id ? fn(p) : p));
}

/** Pure helper — apply `fn` to every pill of `camId`. Same rationale as
 *  `mutatePill` but for cam-wide gestures (RESET cam, nudge whole take,
 *  etc.). */
export function mutatePillsForCam(
  pills: readonly Pill[],
  camId: string,
  fn: (p: Pill) => Pill,
): Pill[] {
  return pills.map((p) => (p.camId === camId ? fn(p) : p));
}

export interface PillsSliceState {
  /** First-class pill list — the unit the user manipulates on the song
   *  timeline. Generated by `loadJob` for every job (single-take = one
   *  pill per cam, arrangement = one per cam-item intersection); user
   *  edits round-trip via `reconcilePills`. */
  pills: Pill[];
  /** UI selection for per-pill editing. null = nothing selected. */
  selectedPillId: string | null;
  /** Long-form arrangement segments — when non-empty, replaces the
   *  single-trim model: useAudioMaster walks these in order with a
   *  gapless crossfade-hop at each segment boundary, and `buildEditSpec`
   *  emits them verbatim into `EditSpec.segments` (the renderer already
   *  supports multi-segment outputs). Empty array = legacy direct-mode
   *  behaviour driven by `trim.in / trim.out`. */
  arrangementSegments: Segment[];
}

export interface PillsSliceActions {
  /** Replace the entire pill list. Used when arrangement reorders or a
   *  fresh job loads. */
  setPills(pills: Pill[]): void;
  /** UI selection target for per-pill edits. */
  setSelectedPillId(id: string | null): void;
  /** Drag-on-timeline: shift a pill's arr-time placement. The pill's
   *  duration is preserved (arrEndS moves with arrStartS). Source-trim
   *  is unchanged — only WHERE on the song the cam plays moves. */
  setPillArrPlacement(id: string, arrStartS: number): void;
  /** Edge-drag the LEFT edge of a pill: shrinks the pill's arr-window
   *  + advances the cam-source-in by the same amount, so the cam still
   *  plays "in sync" with the pill's content. Clamps to a 0.05 s
   *  minimum window and never crosses the right edge. */
  setPillLeftEdgeArrStartS(id: string, arrStartS: number): void;
  /** Edge-drag the RIGHT edge of a pill: shrinks the pill's arr-window
   *  + retreats the cam-source-out by the same amount. Clamps to a 0.05 s
   *  minimum window and never crosses the left edge. */
  setPillRightEdgeArrEndS(id: string, arrEndS: number): void;
  /** Remove a pill. Cuts that target the same camId stay (they may apply
   *  to other pills of that cam). */
  removePill(id: string): void;
  /** Restore a pill's arr/source to its auto-generated default. Useful
   *  when a user-edited pill needs to snap back without nuking the
   *  rest of the arrangement. */
  resetPill(id: string): void;
  /** Restore every pill of a cam to its auto-generated default. */
  resetPillsForCam(camId: string): void;
  /** Nudge a pill's `sourceInS`/`sourceOutS` by `deltaMs` while keeping
   *  its arr-window where it is. The cam plays a slightly earlier /
   *  later excerpt at the same song-position. */
  nudgePillSourceMs(id: string, deltaMs: number): void;
  /** Set a pill's source-trim to `originalSource* + offsetMs/1000`. Lets
   *  the SyncTuner knob drive an absolute per-pill source-offset that
   *  reads symmetrically against the per-pill RESET (which restores the
   *  offset to 0). */
  setPillSourceOffsetMs(id: string, offsetMs: number): void;
  /** Nudge a pill's arr-time placement (shifts `arrStartS` + `arrEndS`
   *  by the same delta). Source-trim is unchanged — only WHERE on the
   *  song the cam plays moves. */
  nudgePillArrMs(id: string, deltaMs: number): void;
  /** Cam-track nudge: shift every pill of `camId` by `deltaMs` in
   *  source-time. Equivalent to calling `nudgePillSourceMs` on each
   *  one, but only one history entry. */
  nudgeCamSourceMs(camId: string, deltaMs: number): void;
  /** Replace the arrangement-segment list. Empty array reverts to
   *  legacy single-trim behaviour. */
  setArrangementSegments(segments: Segment[]): void;
}

export function initialPillsState(): PillsSliceState {
  return {
    pills: [],
    selectedPillId: null,
    arrangementSegments: [],
  };
}

export const createPillsSlice: SliceCreator<PillsSliceActions> = (
  set,
  get,
) => ({
  setPills(pills) {
    // Validate: drop pills referencing missing cams to keep the active-
    // cam lookup well-defined.
    const camIds = new Set(get().clips.map((c) => c.id));
    const next = pills.filter((p) => camIds.has(p.camId));
    const sel = get().selectedPillId;
    const stillSelected = sel != null && next.some((p) => p.id === sel);
    set({ pills: next, selectedPillId: stillSelected ? sel : null });
  },
  setSelectedPillId(id) {
    // Allow null. If non-null but the id no longer exists, treat as null.
    if (id != null) {
      const exists = get().pills.some((p) => p.id === id);
      if (!exists) {
        set({ selectedPillId: null });
        return;
      }
    }
    set({ selectedPillId: id });
  },
  setPillArrPlacement(id, arrStartS) {
    const allPills = get().pills;
    const me = allPills.find((p) => p.id === id);
    if (!me) return;
    const len = me.arrEndS - me.arrStartS;
    // Same-cam neighbour bounds — pills in one cam-lane can't overlap.
    // Computed from the CURRENT positions of siblings (the pill being
    // dragged is excluded so its own placement doesn't pin itself).
    const { prevEnd, nextStart } = neighbourBoundsForCam(allPills, me);
    const safe = clamp(
      Math.max(0, arrStartS),
      prevEnd,
      Math.max(prevEnd, nextStart - len),
    );
    set({
      pills: mutatePill(allPills, id, (p) => ({
        ...p,
        arrStartS: safe,
        arrEndS: safe + len,
        userEdited: true,
      })),
    });
  },
  setPillLeftEdgeArrStartS(id, arrStartS) {
    const allPills = get().pills;
    const me = allPills.find((p) => p.id === id);
    if (!me) return;
    const { prevEnd } = neighbourBoundsForCam(allPills, me);
    const newStart = clamp(arrStartS, prevEnd, me.arrEndS - PILL_MIN_WINDOW_S);
    // The cam stays in sync with the pill: as we trim N seconds off
    // the front of the arr-window, advance sourceInS by the same N.
    // (V1: 1:1 mapping, no time-stretch.)
    const newSourceIn = Math.max(0, me.sourceInS + (newStart - me.arrStartS));
    set({
      pills: mutatePill(allPills, id, (p) => ({
        ...p,
        arrStartS: newStart,
        sourceInS: newSourceIn,
        userEdited: true,
      })),
    });
  },
  setPillRightEdgeArrEndS(id, arrEndS) {
    const allPills = get().pills;
    const me = allPills.find((p) => p.id === id);
    if (!me) return;
    const { nextStart } = neighbourBoundsForCam(allPills, me);
    const newEnd = clamp(arrEndS, me.arrStartS + PILL_MIN_WINDOW_S, nextStart);
    const newSourceOut = Math.max(
      me.sourceInS + 0.001,
      me.sourceOutS + (newEnd - me.arrEndS),
    );
    set({
      pills: mutatePill(allPills, id, (p) => ({
        ...p,
        arrEndS: newEnd,
        sourceOutS: newSourceOut,
        userEdited: true,
      })),
    });
  },
  removePill(id) {
    const pills = get().pills.filter((p) => p.id !== id);
    const sel = get().selectedPillId;
    set({ pills, selectedPillId: sel === id ? null : sel });
  },
  resetPill(id) {
    set({ pills: mutatePill(get().pills, id, restorePillBaseline) });
  },
  resetPillsForCam(camId) {
    set({
      pills: mutatePillsForCam(get().pills, camId, restorePillBaseline),
    });
  },
  nudgePillSourceMs(id, deltaMs) {
    set({
      pills: mutatePill(get().pills, id, (p) => ({
        ...shiftPillSource(p, deltaMs / 1000),
        userEdited: true,
      })),
    });
  },
  setPillSourceOffsetMs(id, offsetMs) {
    const offsetS = offsetMs / 1000;
    set({
      pills: mutatePill(get().pills, id, (p) => {
        // Clamp the OFFSET (not the edges independently) so the window
        // length always survives — same invariant as shiftPillSource
        // (#103).
        const applied = clampSourceShift(p.originalSourceInS, offsetS);
        return {
          ...p,
          sourceInS: p.originalSourceInS + applied,
          sourceOutS: p.originalSourceOutS + applied,
          userEdited: true,
        };
      }),
    });
  },
  nudgePillArrMs(id, deltaMs) {
    const deltaS = deltaMs / 1000;
    set({
      pills: mutatePill(get().pills, id, (p) => {
        const newStart = Math.max(0, p.arrStartS + deltaS);
        return {
          ...p,
          arrStartS: newStart,
          arrEndS: newStart + (p.arrEndS - p.arrStartS),
          userEdited: true,
        };
      }),
    });
  },
  nudgeCamSourceMs(camId, deltaMs) {
    const pills = mutatePillsForCam(get().pills, camId, (p) =>
      shiftPillSource(p, deltaMs / 1000),
    );
    set({ pills });
  },
  setArrangementSegments(segments) {
    set({ arrangementSegments: segments });
  },
});
